import { FC, useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import CN from 'classnames'
import {
  useGetBulkOrderConsignmentDetails,
  useGetICSBulkOrderDetails,
  useICSRetryFailedConsignment,
  useRetryFailedConsignment,
} from 'framework/api/methods'

import { Button, JsonLoader } from 'components/atoms'
import EditShippingAddressModal from 'components/DrawerModals/BulkConsignmentNew/EditShippingAddressModal'
import { DataTable as OrderDetailsTable } from 'components/pages/BulkOrderConsignmentNew/Common/Tables/BulkConsignmentOrdersDetailsTable/DataTable'

export interface BulkConsignmentOrdersDetailsProps {
  [x: string]: any
}

export const BulkConsignmentOrdersDetails: FC<
  BulkConsignmentOrdersDetailsProps
> = ({
  className,
  setRetryFailed,
  splitReferance,
  setFromHistory,
  bulkreferance,
  setbulkreferance,
  getPWBulkConsignmentHistoryList,
  getICSBulkConsignmentHistoryList,
  integrationType,
}: BulkConsignmentOrdersDetailsProps) => {
  const [consignmentOrderDetailList, setConsignmentOrderDetailList] = useState<
    any[]
  >([])
  const [totalRowCount, setTotalRowCount] = useState(0)
  const [tableVariables, setTableVariables] = useState({
    take: 10,
    skip: 0,
    search: '',
  })
  const [pageNumber, setPageNumber] = useState(1)
  const [mainLoadingState, setIsLoading] = useState(false)
  const [openEditShippingAddress, setOpenEditShippingAddress] = useState(false)
  const [retryWmsOrderNo, setRetryWmsOrderNo] = useState({
    wmsOrderNo: '',
    warehouseOrderId: '',
  })

  //  Toaster Function
  const notify = (message: any, type: any) => {
    if (type === 'error') {
      toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      })
    }
    if (type === 'success') {
      toast.success(message, {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      })
    }
  }

  const {
    mutate: getGetBulkOrderConsignmentDetailsMutate,
    isLoading: getGetBulkOrderConsignmentDetailsListIsLoading,
  } = useGetBulkOrderConsignmentDetails()

  /** Process the Get Similar Order List */
  async function getPWBulkOrderConsignmentDetails() {
    setIsLoading(true)
    getGetBulkOrderConsignmentDetailsMutate(
      {
        reference: splitReferance,
        consNo: '',
        carrierService: '',
      },
      {
        onSuccess: ({ data: successData }: any) => {
          const splitRefDetails = successData?.pwBulkConsignmentDetail
          const splitRefOrders = splitRefDetails[0]?.pwBulkConsignmentOrders

          // Sort: 'Failed' status first, others follow (no date sorting)
          const sortedOrders = splitRefOrders
            .slice()
            .sort((a: { status: string }, b: { status: string }) => {
              if (a.status === 'Failed' && b.status !== 'Failed') return -1
              if (a.status !== 'Failed' && b.status === 'Failed') return 1
              return 0 // keep original order if statuses are the same
            })

          // Pagination logic
          const start = tableVariables.skip
          const end = start + tableVariables.take
          const paginatedOrders = sortedOrders.slice(start, end)
          setConsignmentOrderDetailList(paginatedOrders)
          setTotalRowCount(sortedOrders?.length)
          setIsLoading(false)
        },
        onError: ({ response: { data: errData } }: any) => {
          notify(errData.message || 'Error...!', 'error')
          setIsLoading(false)
        },
      },
    )
  }

  //ICS Bulk Consignment Details API Call
  const {
    mutate: getICSBulkOrderConsignmentDetailsMutate,
    isLoading: getICSBulkOrderConsignmentDetailsListIsLoading,
  } = useGetICSBulkOrderDetails()

  /** Process the Get ICS Similar Order List */
  async function getICSBulkOrderConsignmentDetails() {
    setIsLoading(true)
    getICSBulkOrderConsignmentDetailsMutate(
      {
        reference: splitReferance,
        bulkSlipNo: '',
        carrierService: '',
      },
      {
        onSuccess: ({ data: successData }: any) => {
          const splitRefDetails = successData?.icsBulkConsignmentDetail
          const splitRefOrders = splitRefDetails[0]?.icsBulkConsignmentOrders

          // Sort: 'Failed' status first, others follow (no date sorting)
          const sortedOrders = splitRefOrders
            .slice()
            .sort((a: { status: string }, b: { status: string }) => {
              if (a.status === 'Failed' && b.status !== 'Failed') return -1
              if (a.status !== 'Failed' && b.status === 'Failed') return 1
              return 0 // keep original order if statuses are the same
            })

          console.log('Sorted Orders:', sortedOrders)

          // Pagination logic
          const start = tableVariables.skip
          const end = start + tableVariables.take
          const paginatedOrders = sortedOrders.slice(start, end)
          setConsignmentOrderDetailList(paginatedOrders)
          setTotalRowCount(sortedOrders?.length)
          setIsLoading(false)
        },
        onError: ({ response: { data: errData } }: any) => {
          notify(errData.message || 'Error...!', 'error')
          setIsLoading(false)
        },
      },
    )
  }

  useEffect(() => {
    //setFromHistory(false)
    if (integrationType === 'Paperless') {
      getPWBulkOrderConsignmentDetails()
    }
    if (integrationType === 'ICSWms') {
      getICSBulkOrderConsignmentDetails()
    }
  }, [tableVariables.skip, tableVariables.take])

  const customizeClass = CN(
    'w-[350px] px-6 py-4 text-lg font-semibold text-white rounded-lg hover:bg-blue-700 flex items-start justify-start',
  )

  /** API Call for ICS  Bulk Order Retry Failed Consignment */
  const {
    mutate: icsbulkOrderRetryFailedConsignmentMutate,
    isLoading: icsbulkOrderConsignIsLoading,
  } = useICSRetryFailedConsignment()

  /** Process the Bulk Order Consign */
  async function retryICSFailedConsignment(
    splitReference: string,
    wmsOrderNo: string,
  ) {
    setIsLoading(true)
    icsbulkOrderRetryFailedConsignmentMutate(
      {
        splitReference,
        wmsOrderNo,
      },
      {
        onSuccess: ({ data: successData }: any) => {
          notify(successData.message || 'Success', 'success')
          getICSBulkOrderConsignmentDetails()
          setIsLoading(false)
        },
        onError: ({ response: { data: errData } }: any) => {
          notify(errData.message || 'Error...', 'error')
          setIsLoading(false)
        },
      },
    )
  }

  /** API Call for PW  Bulk Order Retry Failed Consignment */
  const {
    mutate: pwbulkOrderRetryFailedConsignmentMutate,
    isLoading: pwbulkOrderConsignIsLoading,
  } = useRetryFailedConsignment()

  /** Process the Bulk Order Consign */
  async function retryFailedConsignment(
    serviceSplitReference: string,
    wmsOrderNo: string,
  ) {
    setIsLoading(true)
    pwbulkOrderRetryFailedConsignmentMutate(
      {
        serviceSplitReference,
        wmsOrderNo,
      },
      {
        onSuccess: ({ data: successData }: any) => {
          notify(successData.message || 'Success', 'success')
          getPWBulkOrderConsignmentDetails()
          setIsLoading(false)
        },
        onError: ({ response: { data: errData } }: any) => {
          notify(errData.message || 'Error...', 'error')
          setIsLoading(false)
        },
        // onError: ({ data: errData }: any) => {
        //   //notify(errData.message || 'Error...!', 'error')
        //   console.log('Failed')
        //   setIsLoading(false)
        // },
      },
    )
  }

  const handleOnBackClick = useCallback(() => {
    if (integrationType === 'Paperless') {
      getPWBulkConsignmentHistoryList()
    }
    if (integrationType === 'ICSWms') {
      getICSBulkConsignmentHistoryList()
    }
    setFromHistory(false)
  }, [])

  const handleRetryClick = useCallback(
    (wmsOrderNo: string) => {
      if (integrationType === 'Paperless') {
        retryFailedConsignment(splitReferance, wmsOrderNo)
      }
      if (integrationType === 'ICSWms') {
        retryICSFailedConsignment(splitReferance, wmsOrderNo)
      }
    },
    [splitReferance],
  )

  return (
    <div
      className={CN(
        'flex items-center justify-center bg-N-50 h-full w-full px-8 py-5',
        {},
        className,
      )}>
      {true && (
        /** Shipment Summery */
        <div className='flex flex-col w-full h-full'>
          <Button
            onClick={(e) => {
              e.preventDefault()
              setRetryFailed({
                isRetry: false,
                serviceSplitReference: '',
              })
              setFromHistory(false)
              setbulkreferance(bulkreferance)
              handleOnBackClick()
            }}
            className='mb-2'
            size='xs'
            appearance='outline'>
            <i className='ri-arrow-left-line pr-3'></i> Back to consignment
            summary
          </Button>

          <div className='flex flex-col h-full w-full rounded-lg bg-white shadow-md px-5 py-7'>
            <div className='flex flex-col w-full h-full bg-white'>
              <h2 className='text-lg font-semibold'>
                Service Split Reference : {splitReferance} - Consigned Order
                Detail Summary
              </h2>
              {/* Loading Screen */}
              {mainLoadingState && (
                <div className='flex w-full h-full items-center justify-center bg-N-800 absolute z-[99] left-0 top-0 bg-opacity-20 cursor-not-allowed'>
                  <JsonLoader size='xs' />
                </div>
              )}
              <div className='flex-1 overflow-auto w-full rounded-lg px-4 py-1'>
                <OrderDetailsTable
                  className=''
                  itemData={consignmentOrderDetailList}
                  tableVariables={tableVariables}
                  setTableVariables={setTableVariables}
                  setPageNumber={setPageNumber}
                  pageNumber={pageNumber}
                  totalRowCount={totalRowCount}
                  setIsLoading={setIsLoading}
                  setOpenEditShippingAddress={setOpenEditShippingAddress}
                  setRetryWmsOrderNo={setRetryWmsOrderNo}
                  handleRetryClick={handleRetryClick}
                  integrationType={integrationType}
                />
              </div>
              {openEditShippingAddress && (
                <EditShippingAddressModal
                  setOpenEditShippingAddress={setOpenEditShippingAddress}
                  orderDetails={retryWmsOrderNo}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

BulkConsignmentOrdersDetails.defaultProps = {}

export default BulkConsignmentOrdersDetails
